import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { AutoComplete, AutoCompleteCompleteEvent } from 'primereact/autocomplete';
import { Calendar } from 'primereact/calendar';
import { InputSwitch } from 'primereact/inputswitch';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Paginator } from 'primereact/paginator';
import { confirmDelete } from 'components/v2/confirm-dialog/confirmDelete';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { toaster } from 'components/v2/toast';
import { DataTableStyled } from 'components/v2/compiler/grids/StyledGrid';
import WizardHTMLEditor from 'components/v2/epd-wizard-inputs/WizardHTMLEditor';
import { withGreyBackground } from 'pages/v2/WithGreyBackground';
import { Container } from 'styles/Styles.styled';
import { H1NewUI, FormPanel } from 'styles/v2/Styles.styled';
import styled from 'styled-components';
import {
  AdminNotificationCreateModel,
  AdminNotificationModel,
  AdminNotificationRecipientModel,
  LookupUserModel,
  createAdminNotification,
  deleteAdminNotification,
  getAdminNotificationCompanies,
  getAdminNotificationRoles,
  getAdminNotificationUsers,
  getAdminNotificationUsersByIds,
  getAdminNotifications,
  updateAdminNotification,
} from 'services/api/adminNotificationsApi';

const CATEGORY_OPTIONS = [
  { label: 'Release notes', value: 'ReleaseNotes' },
  { label: 'News', value: 'News' },
  { label: 'Warning', value: 'Warning' },
  { label: 'System', value: 'System' },
];

interface FormState {
  id?: string;
  title: string;
  body: string;
  category: string;
  publishedAt: Date;
  validFrom?: Date | null;
  validTo?: Date | null;
  isPublished: boolean;
  allUsers: boolean;
  roleIds: string[];
  companyIds: string[];
  userIds: string[];
}

const emptyForm = (): FormState => ({
  title: '',
  body: '',
  category: 'ReleaseNotes',
  publishedAt: new Date(),
  validFrom: null,
  validTo: null,
  isPublished: true,
  allUsers: true,
  roleIds: [],
  companyIds: [],
  userIds: [],
});

const toFormState = (n: AdminNotificationModel): FormState => {
  const all = n.recipients.some((r) => r.recipientType === 'All');
  return {
    id: n.id,
    title: n.title,
    body: n.body,
    category: n.category,
    publishedAt: new Date(n.publishedAt),
    validFrom: n.validFrom ? new Date(n.validFrom) : null,
    validTo: n.validTo ? new Date(n.validTo) : null,
    isPublished: n.isPublished,
    allUsers: all,
    roleIds: n.recipients.filter((r) => r.recipientType === 'Role' && r.roleId).map((r) => r.roleId!),
    companyIds: n.recipients.filter((r) => r.recipientType === 'Company' && r.companyId).map((r) => r.companyId!),
    userIds: n.recipients.filter((r) => r.recipientType === 'User' && r.userId).map((r) => r.userId!),
  };
};

const toCreateModel = (form: FormState): AdminNotificationCreateModel => {
  const recipients: AdminNotificationRecipientModel[] = form.allUsers
    ? [{ recipientType: 'All' }]
    : [
        ...form.roleIds.map<AdminNotificationRecipientModel>((roleId) => ({ recipientType: 'Role', roleId })),
        ...form.companyIds.map<AdminNotificationRecipientModel>((companyId) => ({ recipientType: 'Company', companyId })),
        ...form.userIds.map<AdminNotificationRecipientModel>((userId) => ({ recipientType: 'User', userId })),
      ];

  return {
    title: form.title.trim(),
    body: form.body,
    category: form.category,
    publishedAt: form.publishedAt.toISOString(),
    validFrom: form.validFrom ? form.validFrom.toISOString() : null,
    validTo: form.validTo ? form.validTo.toISOString() : null,
    isPublished: form.isPublished,
    recipients,
  };
};

const PAGE_SIZE = 20;

const AdminNotifications: React.FC = () => {
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);

  const notifications = useQuery({
    queryKey: ['admin', 'notifications', 'list', page],
    queryFn: () => getAdminNotifications(page, PAGE_SIZE),
  });
  const rolesQuery = useQuery({
    queryKey: ['admin', 'notifications', 'roles'],
    queryFn: getAdminNotificationRoles,
    staleTime: 5 * 60_000,
  });
  const companiesQuery = useQuery({
    queryKey: ['admin', 'notifications', 'companies'],
    queryFn: getAdminNotificationCompanies,
    staleTime: 5 * 60_000,
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm());

  const openCreate = () => {
    setForm(emptyForm());
    setDialogOpen(true);
  };
  const openEdit = (n: AdminNotificationModel) => {
    setForm(toFormState(n));
    setDialogOpen(true);
  };

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'notifications', 'list'] });
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  const createMutation = useMutation({
    mutationFn: (data: AdminNotificationCreateModel) => createAdminNotification(data),
    onSuccess: () => {
      invalidate();
      setDialogOpen(false);
      toaster({ severity: 'success', summary: 'Notification created' });
    },
    onError: (e: Error) => toaster({ severity: 'error', summary: 'Failed to create', details: e.message }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: AdminNotificationCreateModel }) => updateAdminNotification(id, data),
    onSuccess: () => {
      invalidate();
      setDialogOpen(false);
      toaster({ severity: 'success', summary: 'Notification updated' });
    },
    onError: (e: Error) => toaster({ severity: 'error', summary: 'Failed to update', details: e.message }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteAdminNotification(id),
    onSuccess: () => {
      invalidate();
      toaster({ severity: 'success', summary: 'Notification deleted' });
    },
    onError: (e: Error) => toaster({ severity: 'error', summary: 'Failed to delete', details: e.message }),
  });

  const onSave = () => {
    if (!form.title.trim()) {
      toaster({ severity: 'warning', summary: 'Title is required' });
      return;
    }
    if (!form.allUsers && form.roleIds.length === 0 && form.companyIds.length === 0 && form.userIds.length === 0) {
      toaster({ severity: 'warning', summary: 'Select at least one role, company, or user, or enable "All users".' });
      return;
    }
    const data = toCreateModel(form);
    if (form.id) {
      updateMutation.mutate({ id: form.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const roles = rolesQuery.data ?? [];
  const companies = companiesQuery.data ?? [];

  const roleOptions = useMemo(
    () => roles.filter((r) => !form.roleIds.includes(r.id)).map((r) => ({ label: r.name, value: r.id })),
    [roles, form.roleIds]
  );
  const companyOptions = useMemo(
    () => companies.filter((c) => !form.companyIds.includes(c.id)).map((c) => ({ label: c.name, value: c.id })),
    [companies, form.companyIds]
  );

  const roleNameLookup = useMemo(() => {
    const m = new Map<string, string>();
    roles.forEach((r) => m.set(r.id, r.name));
    return m;
  }, [roles]);
  const companyNameLookup = useMemo(() => {
    const m = new Map<string, string>();
    companies.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [companies]);

  // User picker (server-side search, since users list can be very large)
  const [userQuery, setUserQuery] = useState('');
  const [userSuggestions, setUserSuggestions] = useState<LookupUserModel[]>([]);
  const [userNameMap, setUserNameMap] = useState<Map<string, LookupUserModel>>(new Map());

  const cacheUsers = (list: LookupUserModel[]) => {
    setUserNameMap((prev) => {
      const next = new Map(prev);
      list.forEach((u) => next.set(u.id, u));
      return next;
    });
  };

  const handleUserSearch = async (e: AutoCompleteCompleteEvent) => {
    const list = await getAdminNotificationUsers(e.query);
    cacheUsers(list);
    setUserSuggestions(list.filter((u) => !form.userIds.includes(u.id)));
  };

  // Hydrate names for user IDs referenced by current form or by table rows.
  useEffect(() => {
    const allReferenced = new Set<string>(form.userIds);
    (notifications.data?.items ?? []).forEach((n) =>
      n.recipients.forEach((r) => {
        if (r.recipientType === 'User' && r.userId) allReferenced.add(r.userId);
      })
    );
    const missing = Array.from(allReferenced).filter((id) => !userNameMap.has(id));
    if (missing.length === 0) return;
    let cancelled = false;
    getAdminNotificationUsersByIds(missing).then((list) => {
      if (!cancelled) cacheUsers(list);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.userIds, notifications.data]);

  const userLabel = (id: string) => {
    const u = userNameMap.get(id);
    if (!u) return id;
    return u.displayName ? `${u.displayName} (${u.email})` : u.email || id;
  };

  const recipientsBody = (n: AdminNotificationModel) => {
    if (n.recipients.some((r) => r.recipientType === 'All')) return 'All users';
    const parts: string[] = [];
    const roleNames = n.recipients
      .filter((r) => r.recipientType === 'Role' && r.roleId)
      .map((r) => roleNameLookup.get(r.roleId!) ?? r.roleId!);
    const companyNames = n.recipients
      .filter((r) => r.recipientType === 'Company' && r.companyId)
      .map((r) => companyNameLookup.get(r.companyId!) ?? r.companyId!);
    const userNames = n.recipients.filter((r) => r.recipientType === 'User' && r.userId).map((r) => userLabel(r.userId!));
    if (roleNames.length > 0) parts.push(`Roles: ${roleNames.join(', ')}`);
    if (companyNames.length > 0) parts.push(`Companies: ${companyNames.join(', ')}`);
    if (userNames.length > 0) parts.push(`Users: ${userNames.join(', ')}`);
    return parts.length > 0 ? parts.join(' · ') : '—';
  };

  return (
    <Container>
      <ConfirmDialog />
      <PageHeader>
        <H1NewUI>Notifications</H1NewUI>
        <Button label="New notification" icon="pi pi-plus" onClick={openCreate} />
      </PageHeader>

      <GridPanel>
        {notifications.isLoading ? (
          <SpinnerWrap>
            <ProgressSpinner />
          </SpinnerWrap>
        ) : (
          <DataTableStyled value={notifications.data?.items ?? []} dataKey="id" emptyMessage="No notifications">
            <Column field="title" header="Title" sortable />
            <Column field="category" header="Category" sortable style={{ width: '140px' }} />
            <Column header="Recipients" body={recipientsBody} />
            <Column
              field="publishedAt"
              header="Published"
              sortable
              style={{ width: '180px' }}
              body={(n: AdminNotificationModel) => new Date(n.publishedAt).toLocaleString()}
            />
            <Column
              header="Status"
              style={{ width: '110px' }}
              body={(n: AdminNotificationModel) =>
                n.isPublished ? <StatusPill $on>Published</StatusPill> : <StatusPill>Draft</StatusPill>
              }
            />
            <Column
              header=""
              style={{ width: '110px', textAlign: 'right' }}
              body={(n: AdminNotificationModel) => (
                <RowActions>
                  <Button icon="pi pi-pencil" className="p-button-text p-button-sm" onClick={() => openEdit(n)} />
                  <Button
                    icon="pi pi-trash"
                    className="p-button-text p-button-sm p-button-danger"
                    onClick={() =>
                      confirmDelete({
                        caption: 'Delete notification?',
                        description: `"${n.title}" will be permanently removed.`,
                        onConfirm: () => deleteMutation.mutate(n.id),
                      })
                    }
                  />
                </RowActions>
              )}
            />
          </DataTableStyled>
        )}
      </GridPanel>

      {(notifications.data?.total ?? 0) > PAGE_SIZE && (
        <Paginator
          first={(page - 1) * PAGE_SIZE}
          rows={PAGE_SIZE}
          totalRecords={notifications.data?.total ?? 0}
          onPageChange={(e) => setPage((e.page ?? 0) + 1)}
        />
      )}

      <Dialog
        visible={dialogOpen}
        onHide={() => setDialogOpen(false)}
        header={form.id ? 'Edit notification' : 'New notification'}
        style={{ width: '820px', maxWidth: '95vw' }}
        modal
      >
        <DialogBody>
          <FormGrid>
            <Field>
              <label>Title *</label>
              <InputText value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={250} />
            </Field>

            <TwoCol>
              <Field>
                <label>Category</label>
                <Dropdown
                  value={form.category}
                  options={CATEGORY_OPTIONS}
                  onChange={(e) => setForm({ ...form, category: e.value })}
                />
              </Field>
              <Field>
                <label>Publish status</label>
                <SwitchRow>
                  <InputSwitch checked={form.isPublished} onChange={(e) => setForm({ ...form, isPublished: !!e.value })} />
                  <span>{form.isPublished ? 'Published' : 'Draft'}</span>
                </SwitchRow>
              </Field>
            </TwoCol>

            <TwoCol>
              <Field>
                <label>Valid from (optional)</label>
                <Calendar
                  value={form.validFrom ?? null}
                  onChange={(e) => setForm({ ...form, validFrom: (e.value as Date) ?? null })}
                  showTime
                  showIcon
                  dateFormat="yy-mm-dd"
                />
              </Field>
              <Field>
                <label>Valid to (optional)</label>
                <Calendar
                  value={form.validTo ?? null}
                  onChange={(e) => setForm({ ...form, validTo: (e.value as Date) ?? null })}
                  showTime
                  showIcon
                  dateFormat="yy-mm-dd"
                />
              </Field>
            </TwoCol>

            <Field>
              <label>Recipients *</label>
              <SwitchRow>
                <InputSwitch
                  checked={form.allUsers}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      allUsers: !!e.value,
                      roleIds: e.value ? [] : form.roleIds,
                      companyIds: e.value ? [] : form.companyIds,
                      userIds: e.value ? [] : form.userIds,
                    })
                  }
                />
                <span>Send to all users</span>
              </SwitchRow>

              {!form.allUsers && (
                <RecipientPickers>
                  <Field>
                    <label>Roles</label>
                    <Dropdown
                      value={null}
                      options={roleOptions}
                      placeholder={rolesQuery.isLoading ? 'Loading…' : 'Add role…'}
                      filter
                      filterBy="label"
                      onChange={(e) => e.value && setForm({ ...form, roleIds: [...form.roleIds, e.value] })}
                    />
                    {form.roleIds.length > 0 && (
                      <ChipRow>
                        {form.roleIds.map((id) => (
                          <Chip key={id}>
                            {roleNameLookup.get(id) ?? id}
                            <button
                              type="button"
                              onClick={() => setForm({ ...form, roleIds: form.roleIds.filter((x) => x !== id) })}
                              aria-label="Remove role"
                            >
                              ×
                            </button>
                          </Chip>
                        ))}
                      </ChipRow>
                    )}
                  </Field>

                  <Field>
                    <label>Companies</label>
                    <Dropdown
                      value={null}
                      options={companyOptions}
                      placeholder={companiesQuery.isLoading ? 'Loading…' : 'Add company…'}
                      filter
                      filterBy="label"
                      virtualScrollerOptions={{ itemSize: 36 }}
                      onChange={(e) => e.value && setForm({ ...form, companyIds: [...form.companyIds, e.value] })}
                    />
                    {form.companyIds.length > 0 && (
                      <ChipRow>
                        {form.companyIds.map((id) => (
                          <Chip key={id}>
                            {companyNameLookup.get(id) ?? id}
                            <button
                              type="button"
                              onClick={() => setForm({ ...form, companyIds: form.companyIds.filter((x) => x !== id) })}
                              aria-label="Remove company"
                            >
                              ×
                            </button>
                          </Chip>
                        ))}
                      </ChipRow>
                    )}
                  </Field>
                </RecipientPickers>
              )}

              {!form.allUsers && (
                <Field style={{ marginTop: 4 }}>
                  <label>Individual users</label>
                  <AutoComplete
                    value={userQuery}
                    suggestions={userSuggestions}
                    completeMethod={handleUserSearch}
                    field="displayName"
                    placeholder="Search users by name or email…"
                    delay={250}
                    itemTemplate={(u: LookupUserModel) => (
                      <div>
                        <div style={{ fontWeight: 500 }}>{u.displayName || u.email}</div>
                        {u.displayName && <div style={{ fontSize: 11, opacity: 0.7 }}>{u.email}</div>}
                      </div>
                    )}
                    onChange={(e) => setUserQuery(typeof e.value === 'string' ? e.value : '')}
                    onSelect={(e) => {
                      const u = e.value as LookupUserModel;
                      if (!form.userIds.includes(u.id)) {
                        setForm({ ...form, userIds: [...form.userIds, u.id] });
                      }
                      setUserQuery('');
                    }}
                  />
                  {form.userIds.length > 0 && (
                    <ChipRow>
                      {form.userIds.map((id) => (
                        <Chip key={id}>
                          {userLabel(id)}
                          <button
                            type="button"
                            onClick={() => setForm({ ...form, userIds: form.userIds.filter((x) => x !== id) })}
                            aria-label="Remove user"
                          >
                            ×
                          </button>
                        </Chip>
                      ))}
                    </ChipRow>
                  )}
                </Field>
              )}
            </Field>

            <Field>
              <label>Body</label>
              <WizardHTMLEditor
                name="body"
                value={form.body}
                onChanged={(_n: string, v: string) => setForm((prev) => ({ ...prev, body: v }))}
                stripPastedStyles={true}
              />
            </Field>
          </FormGrid>

          <DialogFooter>
            <Button label="Cancel" className="p-button-text" onClick={() => setDialogOpen(false)} />
            <Button
              label={form.id ? 'Save' : 'Create'}
              icon="pi pi-check"
              onClick={onSave}
              disabled={createMutation.isPending || updateMutation.isPending}
            />
          </DialogFooter>
        </DialogBody>
      </Dialog>
    </Container>
  );
};

export default withGreyBackground(AdminNotifications);

const PageHeader = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0 1.25rem 0;
`;

const GridPanel = styled(FormPanel)`
  display: block;
  padding: 0;
  overflow: hidden;
`;

const SpinnerWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px;
`;

const RowActions = styled.div`
  display: inline-flex;
  gap: 4px;
`;

const StatusPill = styled.span<{ $on?: boolean }>`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 11px;
  background: ${(p) => (p.$on ? p.theme.colors.secondaryLightGreen30 ?? '#dceedb' : '#eee')};
  color: ${(p) => p.theme.colors.primaryBlack};
`;

const DialogBody = styled.div`
  padding: 0 1.75rem 1.5rem 1.75rem;
`;

const FormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 0 18px 0;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  > label {
    font-weight: 600;
    font-size: 13px;
    color: ${(p) => p.theme.colors.primaryBlack};
  }
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 38px;
`;

const RecipientPickers = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 4px;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
`;

const Chip = styled.span`
  background: ${(p) => p.theme.colors.statusLabelGrayBg ?? '#eef3ed'};
  padding: 2px 6px 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  button {
    background: transparent;
    border: none;
    cursor: pointer;
    font-size: 14px;
    line-height: 1;
    padding: 0 2px;
  }
`;

const DialogFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid ${(p) => p.theme.colors.statusLabelGrayBg ?? '#e0e0e0'};
`;
